import { Alert } from '@mui/material';
import React, { useState } from 'react';
import { formatMoney } from '../../Tools/helpers';

const PaymentModal = ({ selectedMonth, accountStatements, onClose, onPaymentSubmit, creditBalanceFranchis, message, isPaymentReinscription, reinscriptionTotal }) => {
  const defaultAmount = isPaymentReinscription
    ? (reinscriptionTotal ?? '')
    : (accountStatements[selectedMonth]?.summary?.remaining ?? '');

  const [activeTab, setActiveTab] = useState('cash');
  const [transferData, setTransferData] = useState({
    amount: defaultAmount,
    reference: '',
    date: '',
    university_id: null,
    invoice_id: null,
    proof: null
  });
  const [cashData, setCashData] = useState({
    amount: defaultAmount,
    credit_balance: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Manejar cambios en los inputs de transferencia
  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    setTransferData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar cambio de archivo para comprobante
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTransferData(prev => ({
        ...prev,
        proof: file
      }));
    }
  };

  // Manejar cambios en los inputs de tarjeta
  const handleCashChange = (e) => {
    const { name, value } = e.target;
    setCashData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCashChangeCreditBalance = (e) => {
    const { name, value, type, checked } = e.target;
    setCashData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Enviar pago por transferencia
  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onPaymentSubmit({
        type: 'transfer',
        module: isPaymentReinscription ? 'reinscription' : 'month',
        remaining: accountStatements[selectedMonth]?.summary?.remaining || 0,
        ...transferData
      });
    } catch (error) {
      console.error('Error al procesar pago:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enviar pago por tarjeta
  const handleCashSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onPaymentSubmit({
        type: 'cash',
        module: isPaymentReinscription ? 'reinscription' : 'month',
        remaining: accountStatements[selectedMonth]?.summary?.remaining || 0,
        ...cashData
      });
    } catch (error) {
      console.error('Error al procesar pago:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Realizar Pago</h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Pestañas */}
          <div className="flex border-b mb-6">
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === 'cash' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
              }`}
              onClick={() => setActiveTab('cash')}
            >
              Efectivo
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === 'transfer' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
              }`}
              onClick={() => setActiveTab('transfer')}
            >
              Transferencia
            </button>
          </div>

          {activeTab === 'transfer' ? (
            /* Formulario de transferencia */
            <form>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Importe *
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">$</span>
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={transferData.amount}
                      onChange={handleTransferChange}
                      className="block w-full pl-7 rounded border-gray-300 shadow-sm p-2 border"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Folio de transferencia/operación *
                  </label>
                  <input
                    type="text"
                    name="reference"
                    value={transferData.reference}
                    onChange={handleTransferChange}
                    className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2 border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={transferData.date}
                    onChange={handleTransferChange}
                    className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2 border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Adjuntar comprobante *
                  </label>
                  <div className="mt-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                      required
                    />
                  </div>
                  {transferData.proof && (
                    <p className="mt-1 text-xs text-gray-500">
                      Archivo seleccionado: {transferData.proof.name}
                    </p>
                  )}
                </div>
              </div>
              <div className='m-2'>
                {message.status ? (
                    <Alert className="mt-2" severity={message.label}>
                      {message.message}
                    </Alert>
                  ) : (
                    ""
                  )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleTransferSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Procesando...
                    </>
                  ) : 'Pagar'}
                </button>
              </div>
            </form>
          ) : (
            /* Formulario de efectivo */
            <form>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Importe *
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">$</span>
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={cashData.amount}
                      onChange={handleCashChange}
                      className="block w-full pl-7 rounded border-gray-300 shadow-sm p-2 border"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  {/* Checkbox para usar saldo a favor */}
                  {
                    creditBalanceFranchis > 0 ?
                      <div className="mt-5">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="credit_balance"
                            checked={cashData.credit_balance || false}
                            onChange={handleCashChangeCreditBalance}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Usar saldo a favor 
                            <span className='text-blue-600 font-bold ml-3'>
                              {formatMoney(creditBalanceFranchis)}
                            </span>
                          </span>
                        </label>
                      </div>
                    : null
                  }

                  {/* Mensaje de alerta */}
                  <div className='m-2'>
                    {message.status ? (
                      <Alert className="mt-2" severity={message.label}>
                        {message.message}
                      </Alert>
                    ) : ""}
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={handleCashSubmit}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Procesando...
                        </>
                      ) : 'Pagar'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;